<template>
  <div>
    <div v-if="loading" class="text-center py-4">
      <RotateSquare size="60px" class="loading-position animated fadeIn"></RotateSquare>
    </div>

    <div v-else>
      <div class="d-flex justify-content-end align-items-center mb-3">
        <small v-if="dirty" class="text-warning mr-3">
          <i class="fa fa-exclamation-triangle mr-1"></i>Alterações não salvas
        </small>
        <button
          class="btn btn-sm btn-success"
          :disabled="saving"
          @click="save"
        >
          <i class="fa fa-save mr-1"></i>
          {{ saving ? "Salvando..." : "Salvar" }}
        </button>
        <button
          class="btn btn-sm btn-word ml-2"
          :disabled="exporting"
          @click="exportDocx"
        >
          <i class="fa fa-file-word mr-1"></i>
          {{ exporting ? "Exportando..." : "Baixar ficha (.docx)" }}
        </button>
      </div>

      <!-- ── Gestão municipal ─────────────────────────────────────────────── -->
      <div class="form-group">
        <label><strong>Prefeito da Cidade</strong></label>
        <input v-model="form.mayor_name" type="text" class="form-control" placeholder="Nome completo" />
      </div>
      <div class="form-group">
        <label><strong>Vice-Prefeito</strong></label>
        <input v-model="form.vice_mayor_name" type="text" class="form-control" placeholder="Nome completo" />
      </div>
      <div class="form-group">
        <label><strong>Secretaria ou Fundação Responsável pela Elaboração do PMAU</strong></label>
        <input
          v-model="form.responsible_department"
          type="text"
          class="form-control"
          placeholder="Nome completo"
        />
      </div>

      <hr />

      <!-- ── Equipe de Acompanhamento ─────────────────────────────────────── -->
      <h5>Equipe de Acompanhamento</h5>
      <p class="text-muted small">
        Instituições/secretarias/órgãos envolvidos no acompanhamento do plano e as pessoas que os representam.
      </p>

      <div
        v-for="(inst, instIdx) in form.accompanying_institutions"
        :key="'inst-' + instIdx"
        class="card mb-3"
      >
        <div class="card-body">
          <div class="form-group d-flex align-items-end">
            <div class="flex-grow-1">
              <label><strong>Instituição/Secretaria/Órgão</strong></label>
              <input v-model="inst.institution" type="text" class="form-control" />
            </div>
            <button
              class="btn btn-sm btn-danger ml-2 mb-1"
              title="Remover instituição"
              @click="removeInstitution(instIdx)"
            >
              <i class="fa fa-trash"></i>
            </button>
          </div>

          <table class="table table-sm" v-if="inst.people.length">
            <thead>
              <tr>
                <th>Nome completo</th>
                <th>Formação</th>
                <th>Cargo</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(person, personIdx) in inst.people" :key="'person-' + instIdx + '-' + personIdx">
                <td><input v-model="person.name" type="text" class="form-control form-control-sm" /></td>
                <td><input v-model="person.formation" type="text" class="form-control form-control-sm" /></td>
                <td><input v-model="person.role" type="text" class="form-control form-control-sm" /></td>
                <td>
                  <button
                    class="btn btn-sm btn-danger"
                    title="Remover pessoa"
                    @click="removePerson(inst, personIdx)"
                  >
                    <i class="fa fa-times"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <button class="btn btn-sm btn-primary" @click="addPerson(inst)">
            <i class="fa fa-plus mr-1"></i>Adicionar pessoa
          </button>
        </div>
      </div>

      <button class="btn btn-sm btn-primary mb-4" @click="addInstitution">
        <i class="fa fa-plus mr-1"></i>Adicionar instituição
      </button>

      <hr />

      <!-- ── Equipe Técnica Responsável ───────────────────────────────────── -->
      <b-form-checkbox v-model="form.is_private_company" @change="onTogglePrivateCompany">
        Plano elaborado por empresa privada?
      </b-form-checkbox>
      <small class="text-muted d-block mt-1 mb-3">
        Esse item se aplica caso o plano seja elaborado por uma empresa privada. Se o plano for
        elaborado pelo município, esta seção não deve ser preenchida.
      </small>

      <div v-if="form.is_private_company && form.technical_team">
        <h5>Equipe Técnica Responsável</h5>

        <div class="form-group">
          <label><strong>Nome da Empresa</strong></label>
          <input v-model="form.technical_team.company_name" type="text" class="form-control" />
        </div>

        <div class="form-row">
          <div class="form-group col-md-6">
            <label><strong>Diretor Executivo</strong></label>
            <input
              v-model="form.technical_team.executive_director.name"
              type="text"
              class="form-control mb-1"
              placeholder="Nome completo"
            />
            <input
              v-model="form.technical_team.executive_director.formation"
              type="text"
              class="form-control"
              placeholder="Formação"
            />
          </div>
          <div class="form-group col-md-6">
            <label><strong>Coordenação Geral</strong></label>
            <input
              v-model="form.technical_team.general_coordination.name"
              type="text"
              class="form-control mb-1"
              placeholder="Nome completo"
            />
            <input
              v-model="form.technical_team.general_coordination.formation"
              type="text"
              class="form-control"
              placeholder="Formação"
            />
          </div>
        </div>

        <div class="form-group">
          <label><strong>Coordenação Técnica</strong></label>
          <input
            v-model="form.technical_team.technical_coordination.name"
            type="text"
            class="form-control mb-1"
            placeholder="Nome completo"
          />
          <input
            v-model="form.technical_team.technical_coordination.formation"
            type="text"
            class="form-control"
            placeholder="Formação"
          />
        </div>

        <label><strong>Equipe Técnica e de Apoio</strong></label>
        <table class="table table-sm" v-if="form.technical_team.support_team.length">
          <thead>
            <tr>
              <th>Nome completo</th>
              <th>Formação</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(person, idx) in form.technical_team.support_team" :key="'support-' + idx">
              <td><input v-model="person.name" type="text" class="form-control form-control-sm" /></td>
              <td><input v-model="person.formation" type="text" class="form-control form-control-sm" /></td>
              <td>
                <button
                  class="btn btn-sm btn-danger"
                  title="Remover pessoa"
                  @click="removeSupportPerson(idx)"
                >
                  <i class="fa fa-times"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <button class="btn btn-sm btn-primary" @click="addSupportPerson">
          <i class="fa fa-plus mr-1"></i>Adicionar pessoa
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import InstitutionalTeamService from "../../servico/InstitutionalTeamService";
import RotateSquare from "../../components/RotateSquare";

function emptyTechnicalTeam() {
  return {
    company_name: "",
    executive_director: { name: "", formation: "" },
    general_coordination: { name: "", formation: "" },
    technical_coordination: { name: "", formation: "" },
    support_team: []
  };
}

export default {
  name: "PmauInstitutionalTeam",
  components: { RotateSquare },
  props: {
    contaId: { type: String, required: true }
  },
  data() {
    return {
      loading: false,
      saving: false,
      exporting: false,
      dirty: false,
      formLoaded: false,
      form: {
        mayor_name: "",
        vice_mayor_name: "",
        responsible_department: "",
        accompanying_institutions: [],
        is_private_company: false,
        technical_team: null
      }
    };
  },
  watch: {
    form: {
      deep: true,
      handler() {
        if (this.formLoaded) {
          this.dirty = true;
        }
      }
    }
  },
  beforeMount() {
    this.load();
  },
  methods: {
    load() {
      this.loading = true;
      this.formLoaded = false;
      InstitutionalTeamService.Get(this.contaId)
        .then((r) => {
          this.form.mayor_name = r.data.mayor_name || "";
          this.form.vice_mayor_name = r.data.vice_mayor_name || "";
          this.form.responsible_department = r.data.responsible_department || "";
          this.form.accompanying_institutions = r.data.accompanying_institutions || [];
          this.form.is_private_company = r.data.is_private_company || false;
          this.form.technical_team = r.data.technical_team || (this.form.is_private_company ? emptyTechnicalTeam() : null);
        })
        .catch((e) => {
          this.$notify({ data: [e.message], type: "error" });
        })
        .finally(() => {
          this.loading = false;
          this.$nextTick(() => {
            this.dirty = false;
            this.formLoaded = true;
          });
        });
    },
    addInstitution() {
      this.form.accompanying_institutions.push({ institution: "", people: [] });
    },
    removeInstitution(idx) {
      this.form.accompanying_institutions.splice(idx, 1);
    },
    addPerson(inst) {
      inst.people.push({ name: "", formation: "", role: "" });
    },
    removePerson(inst, idx) {
      inst.people.splice(idx, 1);
    },
    onTogglePrivateCompany(checked) {
      if (checked && !this.form.technical_team) {
        this.form.technical_team = emptyTechnicalTeam();
      }
    },
    addSupportPerson() {
      this.form.technical_team.support_team.push({ name: "", formation: "" });
    },
    removeSupportPerson(idx) {
      this.form.technical_team.support_team.splice(idx, 1);
    },
    save() {
      this.saving = true;
      InstitutionalTeamService.Save(this.contaId, this.form)
        .then(() => {
          this.dirty = false;
          this.$notify({ data: ["Ficha institucional salva com sucesso."], type: "success" });
        })
        .catch((e) => {
          this.$notify({ data: [e.message], type: "error" });
        })
        .finally(() => {
          this.saving = false;
        });
    },
    exportDocx() {
      if (this.exporting) return;
      this.exporting = true;
      InstitutionalTeamService.ExportDocx(this.contaId)
        .then((res) => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const a = document.createElement("a");
          a.href = url;
          a.download = "Ficha Institucional e Equipe Técnica.docx";
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch((e) => {
          const msg =
            e && e.response && e.response.status === 404
              ? "Preencha e salve a ficha institucional antes de baixar."
              : "Erro ao exportar a ficha institucional.";
          this.$notify({ data: [msg], type: "error" });
        })
        .finally(() => {
          this.exporting = false;
        });
    }
  }
};
</script>

<style scoped>
.btn-word {
  background-color: #295294;
  border-color: #295294;
  color: #fff;
}
.btn-word:hover:not(:disabled) {
  background-color: #1f4074;
  border-color: #1f4074;
  color: #fff;
}
.btn-word:disabled {
  opacity: 0.65;
}
</style>
